import { useState, useEffect, useMemo } from 'react';
import { ArrowLeft, Tablet } from 'lucide-react';
import { BanepadCourtScreen } from './BanepadCourtScreen';

export interface SingleMatchBanepadViewProps {
  /** All single-match (and other) activities. */
  activities?: any[];
  /** Local planned (booker-picked) single matches. */
  plannedMatches?: any[];
  /** All court bookings (drives each pad's day timeline + linked booking). */
  bookings?: any[];
  /** Booking config (court metadata / names). */
  bookingConfig?: any;
  /** All players (resolves names/ELO on the pads). */
  allPlayers?: any[];
  /** Records a single-match result: (activity, { team1, team2, sets, playedAt }). */
  onRecordSingleMatchResult?: any;
  /** Persists the live (in-progress) score: (activity, liveScore). */
  onLiveSingleMatchScore?: any;
  /** Navigate to a player's profile. */
  onPlayerClick?: any;
  /** Back to the Enkeltkamper list. */
  onBack?: () => void;
  /** Forwarded to <BanepadCourtScreen> (americano slot adapter). */
  americanoMatchToSlots: (m: any) => any;
  /** Live engine wiring, forwarded to <BanepadCourtScreen>. */
  Padel24Engine: any;
  /** End-sound helper, forwarded to <BanepadCourtScreen>. */
  playEndSound: (soundType?: string) => void;
}

// ── Enkeltkamp banepad ──
// The Banepad view for single matches (enkeltkamper) — the analogue of the
// "Banepad" tab inside the KM / seriespill / Americano views. Instead of the
// central dashboard, it shows one full court pad per court that has an active
// enkeltkamp, with the match FORCED on (so it shows regardless of the booking
// time). Result entry writes back through the same handler as everywhere else.
export function SingleMatchBanepadView({
  activities = [],
  plannedMatches = [],
  bookings = [],
  bookingConfig = null,
  allPlayers = [],
  onRecordSingleMatchResult,
  onLiveSingleMatchScore,
  onPlayerClick,
  onBack,
  americanoMatchToSlots,
  Padel24Engine,
  playEndSound,
}: SingleMatchBanepadViewProps) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const courtMeta = bookingConfig?.courtMeta || {};

  // Each live-able enkeltkamp (not cancelled/completed) with a linked court
  // booking → { court, force }. Covers BOTH variants: via-aktiviteter activities
  // (linked via booking.seriesId) and local planned matches (linked via
  // plan.bookingId). `force` is the raw activity/plan passed as forceSingleMatch.
  const pads = useMemo(() => {
    const out: any[] = [];
    (activities || [])
      .filter(
        (a) =>
          a.config?.tournamentType === 'single_match' &&
          a.status !== 'cancelled' &&
          a.status !== 'completed',
      )
      .forEach((a) => {
        const bk = (bookings || []).find((b) => b.seriesId === a.id);
        if (bk) out.push({ key: a.id, court: Number(bk.court), force: a });
      });
    (plannedMatches || [])
      .filter((p) => p.status !== 'cancelled' && p.status !== 'completed')
      .forEach((p) => {
        const bk = (bookings || []).find((b) => b.id === p.bookingId);
        if (bk) out.push({ key: p.id, court: Number(bk.court), force: p });
      });
    return out.sort((x, y) => x.court - y.court);
  }, [activities, plannedMatches, bookings]);

  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8 slide-up">
      <div className="flex items-center justify-between mb-6">
        <div>
          {onBack && (
            <button
              onClick={onBack}
              className="text-xs font-mono text-ink-3 hover:text-ink-2 mb-2 flex items-center gap-1"
            >
              <ArrowLeft size={12} /> Tilbake
            </button>
          )}
          <h1 className="font-display text-3xl font-semibold mb-1">Enkeltkamp — banepad</h1>
          <p className="text-sm text-ink-3">
            {pads.length === 0
              ? 'Ingen enkeltkamper på banen akkurat nå'
              : `${pads.length} ${pads.length === 1 ? 'bane' : 'baner'} med enkeltkamp`}
          </p>
        </div>
      </div>

      {pads.length === 0 ? (
        <div className="card flex flex-col items-center text-center py-16 text-ink-3">
          <Tablet size={28} className="mb-3 opacity-40" />
          <div className="text-sm">
            Ingen aktive enkeltkamper. Reservér en bane og registrer en enkeltkamp, så vises den
            her.
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6">
          {pads.map((p: any) => (
            <div key={p.key} style={{ width: '100%', maxWidth: 760 }}>
              <BanepadCourtScreen
                court={p.court}
                /* One pad per court under this view's own <h1>. */
                headingLevel={2}
                courtMeta={courtMeta}
                bookings={bookings}
                activities={activities}
                plannedMatches={plannedMatches}
                tournament={null}
                activeSeries={null}
                now={now}
                forceSingleMatch={p.force}
                onRecordSingleMatchResult={onRecordSingleMatchResult}
                onLiveSingleMatchScore={onLiveSingleMatchScore}
                onPlayerClick={onPlayerClick}
                allPlayers={allPlayers}
                americanoMatchToSlots={americanoMatchToSlots}
                Padel24Engine={Padel24Engine}
                playEndSound={playEndSound}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
